import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Receipt,
  Hash,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatCurrency } from '@/lib/utils/currency';
import { useBill } from '../hooks';
import { BillStatusBadge } from '../components';
import {
  formatBillType,
  BillDetailsCard,
  BillContractCard,
  BillSystemInfoCard,
  PaymentsCard,
  BillQuickActionsCard,
} from '../components/BillDetailCards';

export function BillDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data: bill, isLoading, isError, error } = useBill(id);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header Skeleton */}
        <div className="bg-white border-b border-slate-200 px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Skeleton className="h-4 w-24 mb-6" />
            <div className="flex items-center gap-6">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-10 w-48" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Content Skeleton */}
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !bill) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <div className="text-red-500 mb-4">
              <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-lg font-medium">
                {(error as Error)?.message || t('failedToLoadBill')}
              </p>
            </div>
            <Link to="/app/bills">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('backToBills')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formattedTotal = formatCurrency(bill.total, {
    locale: 'uz-UZ',
    currency: bill.currency,
  });

  const formattedPaid = formatCurrency(bill.paid, {
    locale: 'uz-UZ',
    currency: bill.currency,
  });

  const remainingAmount = bill.total - bill.paid;
  const formattedRemaining = formatCurrency(remainingAmount, {
    locale: 'uz-UZ',
    currency: bill.currency,
  });

  const isPaid = bill.state === 'PAID';
  const isOverdue = !isPaid && bill.dueAt && new Date(bill.dueAt) < new Date();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Header Section */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          {/* Back Button */}
          <Link
            to="/app/bills"
            className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToBills')}
          </Link>

          {/* Bill Info */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              {/* Icon */}
              <div
                className={`h-16 w-16 rounded-full flex items-center justify-center border-4 border-white shadow-lg ${
                  isPaid
                    ? 'bg-green-100'
                    : isOverdue
                      ? 'bg-red-100'
                      : 'bg-blue-100'
                }`}
              >
                <Receipt
                  className={`h-8 w-8 ${
                    isPaid
                      ? 'text-green-600'
                      : isOverdue
                        ? 'text-red-600'
                        : 'text-blue-600'
                  }`}
                />
              </div>

              {/* Amount and Badges */}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  {formattedTotal}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Bill ID Badge */}
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-300 text-xs font-medium uppercase tracking-wide text-slate-700">
                    <Hash className="h-3.5 w-3.5" />
                    {t('bill')} #{String(bill.id).padStart(5, '0')}
                  </span>
                  {/* Status Badge */}
                  <BillStatusBadge state={bill.state} />
                  {/* Type Badge */}
                  <Badge variant="outline" className="text-xs font-medium">
                    {formatBillType(bill.type)}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6 sm:gap-8">
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('paid')}
              </p>
              <p className="text-xl font-bold text-green-600">{formattedPaid}</p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('remaining')}
              </p>
              <p
                className={`text-xl font-bold ${remainingAmount > 0 ? 'text-orange-600' : 'text-slate-900'}`}
              >
                {formattedRemaining}
              </p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('dueDate')}
              </p>
              <p
                className={`text-xl font-bold ${isOverdue ? 'text-red-600' : 'text-slate-900'}`}
              >
                {bill.dueAt ? format(new Date(bill.dueAt), 'MMM d, yyyy') : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards Grid */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <BillDetailsCard bill={bill} />
          <BillContractCard bill={bill} />
          <BillSystemInfoCard bill={bill} />
          <PaymentsCard bill={bill} />
          <BillQuickActionsCard bill={bill} />
        </div>
      </div>
    </div>
  );
}
